/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const CANCEL_AUTHORITY_TRANSFER_DISCRIMINATOR = 12;

export function getCancelAuthorityTransferDiscriminatorBytes() {
  return getU8Encoder().encode(CANCEL_AUTHORITY_TRANSFER_DISCRIMINATOR);
}

export type CancelAuthorityTransferInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TAccountCurrentAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? WritableAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      TAccountCurrentAuthority extends string
        ? ReadonlySignerAccount<TAccountCurrentAuthority> &
            AccountSignerMeta<TAccountCurrentAuthority>
        : TAccountCurrentAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type CancelAuthorityTransferInstructionData = { discriminator: number };

export type CancelAuthorityTransferInstructionDataArgs = {};

export function getCancelAuthorityTransferInstructionDataEncoder(): FixedSizeEncoder<CancelAuthorityTransferInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: CANCEL_AUTHORITY_TRANSFER_DISCRIMINATOR,
    })
  );
}

export function getCancelAuthorityTransferInstructionDataDecoder(): FixedSizeDecoder<CancelAuthorityTransferInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getCancelAuthorityTransferInstructionDataCodec(): FixedSizeCodec<
  CancelAuthorityTransferInstructionDataArgs,
  CancelAuthorityTransferInstructionData
> {
  return combineCodec(
    getCancelAuthorityTransferInstructionDataEncoder(),
    getCancelAuthorityTransferInstructionDataDecoder()
  );
}

export type CancelAuthorityTransferInput<
  TAccountProgramAuthority extends string = string,
  TAccountCurrentAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
  /** The current program authority */
  currentAuthority: TransactionSigner<TAccountCurrentAuthority>;
};

export function getCancelAuthorityTransferInstruction<
  TAccountProgramAuthority extends string,
  TAccountCurrentAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: CancelAuthorityTransferInput<
    TAccountProgramAuthority,
    TAccountCurrentAuthority
  >,
  config?: { programAddress?: TProgramAddress }
): CancelAuthorityTransferInstruction<
  TProgramAddress,
  TAccountProgramAuthority,
  TAccountCurrentAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: true,
    },
    currentAuthority: {
      value: input.currentAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.programAuthority),
      getAccountMeta(accounts.currentAuthority),
    ],
    data: getCancelAuthorityTransferInstructionDataEncoder().encode({}),
    programAddress,
  } as CancelAuthorityTransferInstruction<
    TProgramAddress,
    TAccountProgramAuthority,
    TAccountCurrentAuthority
  >);
}

export type ParsedCancelAuthorityTransferInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
    /** The current program authority */
    currentAuthority: TAccountMetas[1];
  };
  data: CancelAuthorityTransferInstructionData;
};

export function parseCancelAuthorityTransferInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedCancelAuthorityTransferInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      programAuthority: getNextAccount(),
      currentAuthority: getNextAccount(),
    },
    data: getCancelAuthorityTransferInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
